import React from 'react';

import { Tooltip } from '@mui/material';

import { colors as themeColors } from 'theme/colors';

import { StyledChip, IconLeftContainer, IconRightContainer } from './styles';
import { VatixChipProps } from './types';

/**
 * Vatix Chip component. Use/modify this instead of using the MUI Chip component directly.
 * @param {ChipProps} props - The custom props for Vatix Chip component. Extend MUI Chip props.
 * @example
 * <Chip
 *  label="Chip label"
 *  onClick={chipClick}
 *  onClickRightIcon={deleteChip}
 *  variant="outlined"
 *  iconLeft={<AddFilled />}
 *  colors={{
 *   background: '#FFFFFF',
 *   border: 'rgba(0, 0, 0, 0.12)',
 *   iconLeftBackground: "rgba(2, 136, 209, 0.12)"
 *   iconRightBackground: "rgba(2, 136, 209, 0.12)"
 *   hoverBackground: "rgba(0, 0, 0, 0.12)"
 *  }}
 *  borderStyle="dashed"
 *  enableHover
 * />
 */
const Chip: React.FunctionComponent<VatixChipProps> = ({
  variant = 'filled',
  iconLeft,
  iconLeftGapWidth,
  iconLeftDisableInnerMargin,
  iconRight,
  iconRightGapWidth,
  iconRightDisableInnerMargin,
  onClickRightIcon,
  borderStyle = 'solid',
  enableHover = false,
  capitalizeLabel = true,
  tooltipTitle,
  colors,
  ...props
}) => {
  let labelPaddingLeft = iconLeftGapWidth || (iconLeft ? 6 : 10);
  let labelPaddingRight = iconRightGapWidth || (iconRight ? 6 : 10);
  if (typeof props.label !== 'string') {
    labelPaddingLeft = 0;
    labelPaddingRight = 0;
  }
  const sx: { borderColor: string; borderStyle: 'solid' | 'dashed'; '&&:hover'?: Record<string, string> } = {
    borderColor: colors?.border || themeColors.ui.border.main,
    borderStyle,
  };
  if (enableHover) {
    sx['&&:hover'] = {
      backgroundColor: colors?.hoverBackground ? colors?.hoverBackground : themeColors.divider,
      color: colors?.hoverColor ? colors?.hoverColor : (colors?.foreground ?? '#000000DE'),
      borderColor: colors?.hoverBorder || colors?.border || themeColors.ui.border.main,
      cursor: 'pointer',
    };
  }

  const newOnDelete = onClickRightIcon || (iconRight && props.onClick);

  const chipRef = React.useRef<HTMLDivElement>(null);

  return (
    <Tooltip title={tooltipTitle}>
      <StyledChip
        ref={chipRef}
        variant={variant}
        icon={
          iconLeft ? (
            <IconLeftContainer
              $iconBackgroundColor={colors?.iconLeftBackground}
              $noLabelMargin={props.label === undefined || props.label === ''}
              $disableMargin={iconLeftDisableInnerMargin}
            >
              {iconLeft}
            </IconLeftContainer>
          ) : undefined
        }
        deleteIcon={
          iconRight ? (
            <IconRightContainer
              $iconBackgroundColor={colors?.iconRightBackground}
              $noLabelMargin={props.label === undefined || props.label === ''}
              $disableMargin={iconRightDisableInnerMargin}
            >
              {iconRight}
            </IconRightContainer>
          ) : undefined
        }
        onDelete={newOnDelete ? (event) => newOnDelete({ ...event, currentTarget: chipRef.current }) : undefined}
        sx={sx}
        $colors={{
          foreground: colors?.foreground,
          background: colors?.background,
        }}
        $labelPaddingLeft={labelPaddingLeft}
        $labelPaddingRight={labelPaddingRight}
        $displayPointer={Boolean(props.onClick)}
        $capitalizeLabel={capitalizeLabel}
        {...props}
      />
    </Tooltip>
  );
};

export default Chip;
